import { useCallback, useEffect } from 'react';

import { VIRTUAL_PROPERTY_STATUS } from '../../../../../constants/general';

import type { IProfitCenterRequestItem } from '../../../../../store/slices/valueChainMap/interfaces/IProfitCenterRequestItem';
import type IProfitCenterWithStatuses from '../../../../../store/slices/valueChainMap/interfaces/IProfitCenterWithStatuses';
import type IUseCalculatingStatusPollingParams from '../BusinessStructure/interfaces/IUseCalculatingStatusPollingParams';

const useCalculatingStatusPolling = ({
  data,
  valueChainMapId,
  fetchProfitCenterRecord,
  fetchProfitCenterBoard,
}: IUseCalculatingStatusPollingParams) => {
  const checkCalculatingStatuses = useCallback((responseData: IProfitCenterRequestItem[]): boolean => {
    const checkProfitCenters = (
      profitCenters: IProfitCenterWithStatuses[],
    ): boolean => profitCenters.some((profitCenter) => {
      if (profitCenter.virtualPropertyStatuses) {
        return Object.values(profitCenter.virtualPropertyStatuses).some((statusObj) => {
          const isValidObject = typeof statusObj === 'object' && statusObj !== null && 'status' in statusObj;
          return isValidObject && (statusObj as { status: string }).status === VIRTUAL_PROPERTY_STATUS.CALCULATING;
        });
      }
      return false;
    });

    const checkRecursively = (items: IProfitCenterRequestItem[]): boolean => items.some((item) => {
      const hasCalculatingInParent = checkProfitCenters(
        item.parent as IProfitCenterWithStatuses[],
      );
      const hasCalculatingInChildren = checkRecursively(item.children);

      return hasCalculatingInParent || hasCalculatingInChildren;
    });

    return checkRecursively(responseData);
  }, []);

  useEffect(() => {
    const hasCalculatingStatus = checkCalculatingStatuses(data?.data || []);
    let interval: NodeJS.Timeout | null = null;
    if (data?.data && hasCalculatingStatus) {
      interval = setInterval(() => {
        fetchProfitCenterRecord({ valueChainMapId: valueChainMapId || '' });
        fetchProfitCenterBoard({ valueChainMapId: valueChainMapId || '' });
      }, 30000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [data, checkCalculatingStatuses, fetchProfitCenterRecord, fetchProfitCenterBoard, valueChainMapId]);

  return { checkCalculatingStatuses };
};

export default useCalculatingStatusPolling;
